import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { DayClickEventHandler, DayModifiers } from 'react-day-picker';
import {
  add,
  addDays,
  addMonths,
  differenceInDays,
  eachDayOfInterval,
  endOfDay,
  format,
  isAfter,
  isBefore,
  isMatch,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  max,
  min,
  set,
  startOfDay,
  startOfMonth,
  sub,
  subDays,
  subMonths,
} from 'date-fns';
import { DataRangePreset } from './DateRangePreset';
import { DateRangeInput, FocusType } from './DateRangeInput';
import { DayPickerBase } from '../DayPickerBase';
import { getDatePickerLocale } from '../DatePickerLocale';
import { Caption } from '../Caption';
import { useDatePickerAttributes } from '../hooks/useDatePickerAttributes';
import { Stack } from '../../stack';
import { Button } from '../../button';
import { RadioGroup } from '../../radio';
import { useProviderConfig } from '../../provider';
import { CalendarType, YearMonthCalendar } from './YearMonthCalendar';
import {
  checkInputValidation,
  convertDateToDateWithTime,
  convertStringToDate,
  createOnSelectParams,
  getTime,
  isDisabledTime,
} from './utils';
import { DATE_FORMAT, DATE_HOUR_MINUTE_FORMAT, DATE_HOUR_MINUTE_SECOND_FORMAT } from '../constants';
import { TimePicker } from '../../time-picker';
import { getDisabledTimes, getTimePickerOptions } from '../../time-picker/utils';
import { FormHelperText } from '../../form';
import { DatePickerDisplay } from '../DatePickerDisplay';
import { Months, MonthsProps } from './Months';
import { CaptionLabelType, DatePreset, DatePresetItem, DateRangePickerProps, Dates } from '../types';
import { semantic_spacing } from '../../../foundation';

const DEFAULT_DATA_PRESET = 1;
const MINIMUM_NUMBER_OF_MONTHS = 2;

export const DateRangePickerOld = ({
  width = '100%',
  error,
  disabled,
  className,
  displayTimeFormat,
  size = 'medium',
  showDatePreset = false,
  showRadioGroupDatePreset = false,
  showTodayButton = true,
  showTimePicker = false,
  disabledNowButton = false,
  dates,
  maxDate,
  minDate,
  maxRange,
  numberOfMonths: numberOfMonthsProps = MINIMUM_NUMBER_OF_MONTHS,
  showRemoveButton = false,
  autoCloseable = true,
  opened: openProp,
  customDatePreset,
  disabledDays: disabledDaysProp,
  allowDisabledDaysInRange = false,
  displayMode = 'popover',
  timePickerProps,
  onOpen,
  onClose,
  onChange,
  onSelect,
  zIndex,
}: DateRangePickerProps) => {
  const isBottomSheetPicker = displayMode === 'bottomSheet';
  const { locale } = useProviderConfig();
  const { cellSize, footer } = useDatePickerAttributes(displayMode);
  const {
    container: { style: footerStyle, ...footerProps },
  } = footer;
  const [opened, setOpened] = useState<boolean>(Boolean(openProp));
  const [showRemoveIcon, setShowRemoveIcon] = useState(false);
  const [placeholderValue, setPlaceholderValue] = useState<string>('');
  const [focused, setFocused] = useState<FocusType>(null);
  const [timePickerFocused, setTimePickerFocused] = useState(false);
  const [calendarType, setCalendarType] = useState<CalendarType>('day');
  const [inputValue, setInputValue] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [errorStatus, setErrorStatus] = useState<boolean>(Boolean(error));
  const datePickerLocale = getDatePickerLocale(locale?.lang);
  const datePresetLocale = locale?.DatePickerRange.dataPreset;
  const enableDatePreset = showDatePreset || showRadioGroupDatePreset;
  const isUnControlled = !autoCloseable || isBottomSheetPicker;
  const isShowTodayButton = !isBottomSheetPicker && !showDatePreset && showTodayButton && !showTimePicker;
  const isShowTimePickerFooter = !showDatePreset && showTimePicker;

  const today = useRef<Date>(new Date()).current;
  const defaultDatePreset: DatePreset = customDatePreset ?? [
    { id: 0, label: datePresetLocale?.today ?? '', range: [today, today] },
    { id: 1, label: datePresetLocale?.['1-week'] ?? '', range: [sub(today, { days: 6 }), today] },
    { id: 2, label: datePresetLocale?.['1-month'] ?? '', range: [sub(today, { months: 1, days: 1 }), today] },
    { id: 3, label: datePresetLocale?.['3-month'] ?? '', range: [sub(today, { months: 3, days: 1 }), today] },
    { id: 4, label: datePresetLocale?.['6-month'] ?? '', range: [sub(today, { months: 6, days: 1 }), today] },
    { id: 5, label: datePresetLocale?.['1-year'] ?? '', range: [sub(today, { months: 12, days: 1 }), today] },
    { id: 6, label: datePresetLocale?.all ?? '', range: [new Date('2000-01-01'), today] },
  ];

  const initialDays = useMemo(
    () => ({
      from: dates?.from,
      to: dates?.to,
    }),
    [dates],
  );

  const numberOfMonths = useMemo(() => {
    return isBottomSheetPicker || showTimePicker
      ? 1
      : numberOfMonthsProps < MINIMUM_NUMBER_OF_MONTHS
      ? MINIMUM_NUMBER_OF_MONTHS
      : numberOfMonthsProps;
  }, [displayMode, numberOfMonthsProps]);

  const mergedDisabled = useMemo<[boolean, boolean]>(() => {
    if (Array.isArray(disabled)) {
      return disabled;
    }

    return [disabled || false, disabled || false];
  }, [disabled]);

  const disabledDatePreset = useMemo(() => {
    if (
      mergedDisabled[0] === true &&
      mergedDisabled[1] === false &&
      !isSameDay(convertStringToDate(inputValue.start), today)
    ) {
      return true;
    } else if (
      mergedDisabled[0] === false &&
      mergedDisabled[1] === true &&
      !isSameDay(convertStringToDate(inputValue.end), today)
    ) {
      return true;
    } else {
      return false;
    }
  }, [inputValue, mergedDisabled]);

  const dateFormat = useMemo(() => {
    if (displayTimeFormat === 'hourMinuteSecond' || (showTimePicker && !displayTimeFormat)) {
      return DATE_HOUR_MINUTE_SECOND_FORMAT;
    }
    if (displayTimeFormat === 'hourMinute') {
      return DATE_HOUR_MINUTE_FORMAT;
    }

    return DATE_FORMAT;
  }, [displayTimeFormat, showTimePicker]);

  const [currentMonth, setCurrentMonth] = useState<Date | undefined>(initialDays.to ?? new Date());
  const [selectedDateRange, setSelectedDateRange] = useState<Dates>(initialDays);
  const [selectedTimeRange, setSelectedTimeRange] = useState<Dates>(initialDays);
  const [selectedRangePreset, setSelectedRangePreset] = useState<number | null>(
    enableDatePreset ? DEFAULT_DATA_PRESET : null,
  );

  const disabledTime = useCallback(() => {
    return timePickerProps?.disabledTime
      ? focused === 'start'
        ? timePickerProps.disabledTime(inputValue.start ? convertStringToDate(inputValue.start) : today, 'from')
        : timePickerProps.disabledTime(inputValue.end ? convertStringToDate(inputValue.end) : today, 'to')
      : null;
  }, [focused, timePickerProps?.disabledTime, inputValue]);

  const {
    disabledSelectedTime,
    disabledSelectedTimeButton,
    fromTimeHourPickerOptions,
    fromTimeMinutePickerOptions,
    toTimeHourPickerOptions,
    toTimeMinutePickerOptions,
  } = useMemo(() => {
    const fromDisabledTimes = getDisabledTimes(
      // @ts-expect-error: TS2345
      disabledTime,
      inputValue.start ? convertStringToDate(inputValue.start) : today,
      'from',
    );
    const toDisabledTimes = getDisabledTimes(
      // @ts-expect-error: TS2345
      disabledTime,
      inputValue.end ? convertStringToDate(inputValue.end) : today,
      'to',
    );
    const incrementMinute = timePickerProps?.incrementMinute || 1;

    const fromTimeHourPickerOptions = getTimePickerOptions(24, 1, fromDisabledTimes.disabledHours);
    const fromTimeMinutePickerOptions = getTimePickerOptions(60, incrementMinute, fromDisabledTimes.disabledMinutes);
    const toTimeHourPickerOptions = getTimePickerOptions(24, 1, toDisabledTimes.disabledHours);
    const toTimeMinutePickerOptions = getTimePickerOptions(60, incrementMinute, toDisabledTimes.disabledMinutes);

    const fromDisabledTime = isDisabledTime(inputValue.start, fromTimeHourPickerOptions, fromTimeMinutePickerOptions);
    const toDisabledTime = isDisabledTime(inputValue.end, toTimeHourPickerOptions, toTimeMinutePickerOptions);

    if (
      disabledTime === null ||
      (focused === null && !showTimePicker && !inputValue.start && !inputValue.end) ||
      !checkInputValidation(inputValue.start, dateFormat, true) ||
      !checkInputValidation(inputValue.end, dateFormat, true)
    ) {
      return {
        disabledSelectedTime: false,
        disabledSelectedTimeButton: false,
        fromTimeHourPickerOptions,
        fromTimeMinutePickerOptions,
        toTimeHourPickerOptions,
        toTimeMinutePickerOptions,
      };
    }

    return {
      disabledSelectedTime: fromDisabledTime || toDisabledTime,
      disabledSelectedTimeButton: focused === 'start' ? fromDisabledTime : toDisabledTime,
      fromTimeHourPickerOptions,
      fromTimeMinutePickerOptions,
      toTimeHourPickerOptions,
      toTimeMinutePickerOptions,
    };
  }, [showTimePicker, focused, timePickerProps, inputValue, disabledTime]);

  const modifiers = useMemo(() => {
    return {
      start: selectedDateRange.from ? startOfDay(selectedDateRange.from) : null,
      end: selectedDateRange.to ? endOfDay(selectedDateRange.to) : null,
    };
  }, [selectedDateRange]);

  const handleInitial = (reset: boolean) => {
    if (!opened) {
      const initialDates = getInitialDate(initialDays);
      const newInputValue = initialDates.inputValue;
      const newDate = reset ? initialDates.resetDate : initialDays;

      setErrorStatus(Boolean(error));
      setInputValue({ start: newInputValue.from, end: newInputValue.to });
      setSelectedDateRange(newDate);
      showTimePicker && setSelectedTimeRange(newDate);
      setCurrentMonth(newDate.from);
      setCurrentPreset();
      reset && onChange?.(newDate.from, newDate.to);
    }
  };

  const handleClose = () => {
    onClose?.();
    setOpened(false);
  };

  useEffect(() => {
    if (!opened) {
      setErrorStatus(Boolean(error));
      setCalendarType('day');
      setFocused(null);
      setPlaceholderValue('');
    } else {
      const initialFocus = focused || (initialDays.from && !initialDays.to ? 'end' : 'start');
      setFocused(initialFocus);

      if (initialDays.from && initialFocus === 'start') {
        setCurrentMonth(initialDays.from);
      } else if (initialDays.to && initialFocus === 'end') {
        const changed_month = isBottomSheetPicker ? initialDays.to : subMonths(initialDays.to, numberOfMonths - 1);
        // @ts-expect-error: TS2345
        setCurrentMonth(isSameMonth(initialDays.from, initialDays.to) ? initialDays.from : changed_month);
      }
    }
  }, [opened]);

  useEffect(() => {
    if (modifiers.start && focused === 'start') {
      setCurrentMonth(modifiers.start);
    } else if (modifiers.end && focused === 'end') {
      const changed_month = isBottomSheetPicker ? modifiers.end : subMonths(modifiers.end, numberOfMonths - 1);
      // @ts-expect-error: TS2345
      setCurrentMonth((isSameMonth(modifiers.start, modifiers.end) ? modifiers.start : changed_month) ?? undefined);
    }
  }, [focused, modifiers]);

  useEffect(() => {
    setErrorStatus(Boolean(error));
  }, [error]);

  const getInitialDate = ({ from, to }: { from?: Date; to?: Date }) => {
    if (mergedDisabled[0] === false && mergedDisabled[1] === true) {
      return {
        // @ts-expect-error: TS2345
        inputValue: { from: '', to: format(to, dateFormat) },
        resetDate: { from: undefined, to: initialDays.to },
      };
    } else if (mergedDisabled[0] === true && mergedDisabled[1] === false) {
      return {
        // @ts-expect-error: TS2345
        inputValue: { from: format(from, dateFormat), to: '' },
        resetDate: { from: initialDays.from, to: undefined },
      };
    } else {
      return { inputValue: { from: '', to: '' }, resetDate: { from: undefined, to: undefined } };
    }
  };

  const handleChangeDate = (day: Date, modifiers: DayModifiers): DayClickEventHandler | undefined => {
    if (modifiers.disabled && focused === null) return;
    // 아무 것도 선택되지 않은 상태에서는 start -> end 플로우를 무조건 사용하는 것으로 가정
    const focusedValue = focused ?? 'start';

    const isNeedResetTo =
      focused == null ||
      (selectedDateRange.to
        ? isAfter(day, selectedDateRange.to) ||
          (maxRange && isAfter(selectedDateRange.to, calculateMinMaxRangeDate(day, 'start')))
        : false);
    const isNeedResetFrom = selectedDateRange.from
      ? isBefore(day, selectedDateRange.from) ||
        (maxRange && isBefore(selectedDateRange.from, calculateMinMaxRangeDate(day, 'end')))
      : false;

    const { from, to } = {
      ...(focusedValue === 'start' && {
        from: showTimePicker
          ? inputValue.start
            ? convertDateToDateWithTime(day, convertStringToDate(inputValue.start))
            : day
          : day,
        to: isNeedResetTo ? undefined : inputValue.end ? convertStringToDate(inputValue.end) : undefined,
      }),
      ...(focusedValue === 'end' && {
        from: isNeedResetFrom ? undefined : inputValue.start ? convertStringToDate(inputValue.start) : undefined,
        to: showTimePicker
          ? inputValue.end
            ? convertDateToDateWithTime(day, convertStringToDate(inputValue.end), { seconds: 59, milliseconds: 999 })
            : day
          : day,
      }),
    };
    // @ts-expect-error: TS2345
    setInputValue({
      ...(focusedValue === 'start' && {
        // @ts-expect-error: TS2345
        start: format(from, dateFormat),
        end: isNeedResetTo ? '' : inputValue.end,
      }),
      ...(focusedValue === 'end' && {
        start: isNeedResetFrom ? '' : inputValue.start,
        // @ts-expect-error: TS2345
        end: format(to, dateFormat),
      }),
    });
    // @ts-expect-error: TS2345
    const currentStartOfMonth = startOfMonth(currentMonth);
    const isChangeCurrentMonth =
      isAfter(day, currentStartOfMonth) || isBefore(currentStartOfMonth, addMonths(day, numberOfMonths));

    setSelectedDateRange({ from, to });
    setSelectedRangePreset(null);

    if (typeof onSelect === 'function') {
      const { selected_from, selected_to } = createOnSelectParams(from, to, showTimePicker, selectedTimeRange);
      onSelect(selected_from, selected_to, focused);
    }

    if (!isUnControlled && !showTimePicker && from && to) {
      onChange?.(startOfDay(from), endOfDay(to));
      setOpened(false);
      setFocused(null);
      return;
    }

    if (showTimePicker) {
      setSelectedTimeRange({
        from,
        to,
      });
    }

    if (isChangeCurrentMonth) {
      const isOverMonth = to != null && !isSameMonth(to, currentStartOfMonth) && to.getTime() === day.getTime();
      setCurrentMonth(isOverMonth ? subMonths(day, numberOfMonths - 1) : day);
    }

    if (!showTimePicker && focusedValue === 'start' && !to) {
      setFocused('end');
    }

    if (!showTimePicker && focusedValue === 'end' && !from) {
      setFocused('start');
    }
  };

  const calculateTimeForOnDayClick = (day: Date) => {
    const defaultTime = timePickerProps?.defaultTime;
    if (
      showTimePicker &&
      focused === 'start' &&
      (fromTimeHourPickerOptions?.length > 0 || fromTimeMinutePickerOptions?.length > 0)
    ) {
      return set(day, {
        hours: getTime(fromTimeHourPickerOptions, defaultTime?.start.hour),
        minutes: getTime(fromTimeMinutePickerOptions, defaultTime?.start.minute),
      });
    } else if (
      showTimePicker &&
      focused === 'end' &&
      (toTimeHourPickerOptions?.length > 0 || toTimeMinutePickerOptions?.length > 0)
    ) {
      return set(day, {
        hours: getTime(toTimeHourPickerOptions, defaultTime?.end.hour),
        minutes: getTime(toTimeMinutePickerOptions, defaultTime?.end.minute),
      });
    } else {
      return day;
    }
  };

  const getDataPresetRangeValue = ({ range }: Partial<DatePresetItem>) => {
    // @ts-expect-error: TS2345
    const diffDays = differenceInDays(range?.[1], range?.[0]);
    const is_today = diffDays === 0;

    if (mergedDisabled[0] === false && mergedDisabled[1] === true) {
      const currentEndDate = convertStringToDate(inputValue.end);
      const newStartDate = sub(currentEndDate, { days: diffDays });
      return {
        startDate: startOfDay(is_today ? today : newStartDate),
        endDate: currentEndDate,
      };
    } else if (mergedDisabled[0] === true && mergedDisabled[1] === false) {
      const currentStartDate = convertStringToDate(inputValue.start);
      const newEndDate = add(currentStartDate, { days: diffDays });

      return {
        startDate: currentStartDate,
        endDate: endOfDay(is_today ? today : newEndDate),
      };
    } else {
      // @ts-expect-error: TS2345
      return { startDate: startOfDay(range?.[0]), endDate: endOfDay(range?.[1]) };
    }
  };

  const handleDataPresetRangeClick = ({ range, id, close }: Partial<DatePresetItem> & { close?: () => void }) => {
    const { startDate, endDate } = getDataPresetRangeValue({ range });
    const start_date_with_set_time = set(startDate, {
      seconds: 0,
      milliseconds: 0,
    });
    const end_date_with_set_time = set(endDate, {
      seconds: 59,
      milliseconds: 999,
    });

    setSelectedRangePreset(id ?? null);
    setSelectedDateRange({
      from: startDate,
      to: endDate,
    });

    if (showTimePicker) {
      setSelectedTimeRange({
        from: start_date_with_set_time,
        to: end_date_with_set_time,
      });
    }

    const { from, to } = showTimePicker
      ? {
          from: start_date_with_set_time,
          to: end_date_with_set_time,
        }
      : { from: startDate, to: endDate };

    onChange?.(from, to);
    close?.();
  };

  const setCurrentPreset = () => {
    if (enableDatePreset) {
      if (!initialDays.from || !initialDays.to) {
        setSelectedRangePreset(null);
        return;
      }

      const currentDatePreset = defaultDatePreset.find(({ id, range }) => {
        return modifiers
          ? // @ts-expect-error: TS2345
            format(range[0], dateFormat) === format(initialDays.from, dateFormat) &&
              // @ts-expect-error: TS2345
              format(range[1], dateFormat) === format(initialDays.to, dateFormat)
          : id === DEFAULT_DATA_PRESET;
      });

      if (currentDatePreset) {
        setSelectedRangePreset(currentDatePreset.id);
        setSelectedDateRange({
          from: startOfDay(currentDatePreset.range[0]),
          to: endOfDay(currentDatePreset.range[1]),
        });
      }
    }
  };

  const disabledOutOfRangeDays = useCallback(
    (day: Date) => {
      if (modifiers.start && modifiers.end) {
        return false;
      }
      if (focused === 'start' && modifiers.end) {
        return isAfter(day, modifiers.end);
      }
      if (focused === 'end' && modifiers.start) {
        return isBefore(day, modifiers.start);
      }
      return false;
    },
    [focused, modifiers],
  );

  const calculateMinMaxRangeDate = (day: Date, dayType: 'start' | 'end') => {
    if (maxRange) {
      const duration = {};
      // @ts-expect-error: TS7053
      duration[maxRange[1]] = maxRange[0];

      if (dayType === 'start') {
        const rangeMax = add(day, duration);
        return maxDate ? min([new Date(maxDate), rangeMax]) : rangeMax;
      }

      const rangeMin = sub(day, duration);
      return minDate ? max([new Date(minDate), rangeMin]) : rangeMin;
    }
    return day;
  };

  const disabledDays = useCallback(
    (day: Date, useDisabledOutOfRange = true) => {
      // disabledDaysProp 지정된 케이스
      if (disabledDaysProp != null) {
        if (disabledDaysProp(day)) {
          return true;
        }
        // 날짜가 선택된 경우 해당 날짜로부터 연속하는 값만 선택 가능
        // TODO: O(n^2)의 시간 복잡도를 가지고 있으므로 최적화 필요
        const isSelecting =
          (selectedDateRange.from != null && selectedDateRange.to == null) ||
          (selectedDateRange.from == null && selectedDateRange.to != null);
        if (useDisabledOutOfRange && isSelecting && !allowDisabledDaysInRange) {
          const betweenDays = eachDayOfInterval({
            start: min([selectedDateRange.from ?? day, day]),
            end: max([selectedDateRange.to ?? day, day]),
          });
          if (betweenDays.some((day) => disabledDaysProp(day))) {
            return true;
          }
        }
      }
      const currentDay = new Date(day);
      // @ts-expect-error: TS2345
      let maxRangeDay = new Date(maxDate);
      // @ts-expect-error: TS2345
      let minRangeDay = new Date(minDate);

      if (maxRange) {
        maxRangeDay = calculateMinMaxRangeDate(selectedDateRange.from ?? currentDay, 'start');
        minRangeDay = calculateMinMaxRangeDate(selectedDateRange.to ?? currentDay, 'end');
      }
      const maxResult = endOfDay(maxRangeDay);
      const minResult = startOfDay(minRangeDay);

      if (maxRange && minDate) {
        const min_date = selectedDateRange.from && selectedDateRange.to ? startOfDay(new Date(minDate)) : minResult;

        return (
          isBefore(currentDay, min_date) ||
          isAfter(currentDay, maxResult) ||
          (useDisabledOutOfRange && disabledOutOfRangeDays(currentDay))
        );
      }

      if (maxRange) {
        return selectedDateRange.from && selectedDateRange.to
          ? useDisabledOutOfRange && disabledOutOfRangeDays(currentDay)
          : isBefore(currentDay, minResult) ||
              isAfter(currentDay, maxResult) ||
              (useDisabledOutOfRange && disabledOutOfRangeDays(currentDay));
      }
      if (maxDate && minDate) {
        return (
          isBefore(currentDay, minResult) ||
          isAfter(currentDay, maxResult) ||
          (useDisabledOutOfRange && disabledOutOfRangeDays(currentDay))
        );
      }
      if (maxDate) {
        return isAfter(currentDay, maxResult) || (useDisabledOutOfRange && disabledOutOfRangeDays(currentDay));
      }
      if (minDate) {
        return isBefore(currentDay, minResult) || (useDisabledOutOfRange && disabledOutOfRangeDays(currentDay));
      }

      if (mergedDisabled[0] === true && mergedDisabled[1] === false) {
        return isBefore(currentDay, convertStringToDate(inputValue.start));
      }

      if (mergedDisabled[0] === false && mergedDisabled[1] === true) {
        return isAfter(currentDay, convertStringToDate(inputValue.end));
      }

      return useDisabledOutOfRange && disabledOutOfRangeDays(currentDay);
    },
    [disabledDaysProp, maxDate, minDate, selectedDateRange, disabledOutOfRangeDays],
  );

  useEffect(() => {
    if (initialDays) {
      setInputValue({
        start: initialDays.from ? format(initialDays.from, dateFormat) : '',
        end: initialDays.to ? format(initialDays.to, dateFormat) : '',
      });
      setSelectedDateRange({
        from: initialDays.from ? convertDateToDateWithTime(initialDays.from) : initialDays.from,
        to: initialDays.to ? convertDateToDateWithTime(initialDays.to) : initialDays.to,
      });
      showTimePicker && setSelectedTimeRange(initialDays);
      setCurrentPreset();
    }
  }, [initialDays, opened, showTimePicker]);

  const renderToYearMonthCalendar = () => (
    <YearMonthCalendar
      type={calendarType}
      displayMode={displayMode}
      selectedRange={selectedDateRange}
      disabledDates={disabledDays}
      // @ts-expect-error: TS2322
      currentDate={currentMonth}
      locale={datePickerLocale}
      onChange={(date) => setCurrentMonth(date ?? undefined)}
      onClose={() => setCalendarType('day')}
    />
  );

  const getRangeHoveredList = () => {
    if (!placeholderValue && !(selectedRangeLabel.from || selectedRangeLabel.to)) {
      return [];
    }
    // @ts-expect-error: TS2345
    const from = convertDateToDateWithTime(selectedRangeLabel.from);
    // @ts-expect-error: TS2345
    const to = convertDateToDateWithTime(selectedRangeLabel.to);
    const hoveredDate = convertStringToDate(placeholderValue).setHours(0, 0, 0, 0);

    if (focused === 'start' && to && isBefore(hoveredDate, addDays(to, 1))) {
      return eachDayOfInterval({ start: hoveredDate, end: to });
    }
    if (focused === 'end' && from && isAfter(hoveredDate, subDays(from, 1))) {
      return eachDayOfInterval({ start: from, end: hoveredDate });
    }

    return [];
  };

  const getRangeInsideHoveredList = () => {
    const { from: selected_from, to: selected_to } = selectedRangeLabel;
    if (!placeholderValue || !(selected_from && selected_to) || isSameDay(selected_from, selected_to)) {
      return [];
    }
    // @ts-expect-error: TS2345
    const from = convertDateToDateWithTime(selectedRangeLabel.from);
    // @ts-expect-error: TS2345
    const to = convertDateToDateWithTime(selectedRangeLabel.to);
    const hoveredDate = convertStringToDate(placeholderValue).setHours(0, 0, 0, 0);

    if (
      !isWithinInterval(hoveredDate, { start: from, end: to }) ||
      isBefore(hoveredDate, from) ||
      isAfter(hoveredDate, to)
    ) {
      return [];
    }

    if (focused === 'start') {
      return eachDayOfInterval({ start: addDays(hoveredDate, 1), end: addDays(to, 1) });
    }
    if (focused === 'end') {
      return eachDayOfInterval({ start: subDays(from, 1), end: subDays(hoveredDate, 1) });
    }

    return [];
  };

  /**
   * @param value
   * @param strongCheck value empty case, value.length 가 dateFormat 보다 적은 케이스 error 판단 유무.
   */
  const checkValidation = (value: string, strongCheck = false) => {
    if (!checkInputValidation(value, dateFormat, strongCheck)) {
      return false;
    }

    if (checkInputValidation(value, dateFormat, true) && disabledDays(convertStringToDate(value), false)) {
      return false;
    }

    return true;
  };

  const selectedRangeLabel = useMemo(() => {
    const from = checkValidation(inputValue.start, true) ? convertStringToDate(inputValue.start) : null;
    const to = checkValidation(inputValue.end, true) ? convertStringToDate(inputValue.end) : null;

    return {
      from: from ?? to,
      to,
    };
  }, [inputValue, selectedDateRange, checkValidation]);

  const handleChangeTime = ({ time, isToday }: { time: Date | null; isToday: boolean }) => {
    const { from, to } = selectedDateRange;
    const newTime = isToday ? new Date() : time;

    const { dateFrom, dateTo, timeFrom, timeTo } = {
      ...(focused === 'start' && {
        dateFrom: convertDateToDateWithTime(from === undefined || isToday ? today : from),
        dateTo: to,
        timeFrom: convertDateToDateWithTime(from === undefined ? today : from, newTime),
        timeTo:
          to === undefined
            ? to
            : convertDateToDateWithTime(to, selectedTimeRange.to, { seconds: 59, milliseconds: 999 }),
      }),
      ...(focused === 'end' && {
        dateFrom: from,
        dateTo: convertDateToDateWithTime(to === undefined || isToday ? today : to),
        timeFrom: from === undefined ? from : convertDateToDateWithTime(from, selectedTimeRange.from),
        timeTo: convertDateToDateWithTime(to === undefined ? today : to, newTime, {
          seconds: 59,
          milliseconds: 999,
        }),
      }),
    };
    // @ts-expect-error: TS2345
    const isNeedResetFrom = timeFrom ? isBefore(timeTo, timeFrom) : false;
    // @ts-expect-error: TS2345
    const isNeedResetTo = timeTo ? isAfter(timeFrom, timeTo) : false;

    setSelectedDateRange({
      from: dateFrom,
      to: dateTo,
    });

    setSelectedTimeRange({ from: timeFrom, to: timeTo });
    // @ts-expect-error: TS2345
    setInputValue({
      ...(focused === 'start' && {
        // @ts-expect-error: TS2345
        start: format(convertDateToDateWithTime(dateFrom, timeFrom), dateFormat),
        end: isNeedResetTo ? '' : inputValue.end,
      }),
      ...(focused === 'end' && {
        start: isNeedResetFrom ? '' : inputValue.start,
        end: format(
          // @ts-expect-error: TS2345
          convertDateToDateWithTime(dateTo, timeTo, {
            seconds: 59,
            milliseconds: 999,
          }),
          dateFormat,
        ),
      }),
    });

    if (typeof onSelect === 'function') {
      const { selected_from, selected_to } = createOnSelectParams(dateFrom, dateTo, showTimePicker, {
        from: timeFrom,
        to: timeTo,
      });
      onSelect(selected_from, selected_to, focused);
    }
  };

  const handleTimePickerFooterClick = () => {
    const { from, to } = selectedDateRange;
    if (from && to && inputValue.start && inputValue.end) {
      const newTimeStartDate = convertDateToDateWithTime(from, selectedTimeRange.from);
      const newTimeEndtDate = convertDateToDateWithTime(to, selectedTimeRange.to, {
        seconds: 59,
        milliseconds: 999,
      });
      onChange?.(newTimeStartDate, newTimeEndtDate);
      setOpened(false);
      setFocused(null);
      handleClose();
      return;
    }

    if (focused === 'start' && (!to || inputValue.end === '')) {
      setFocused('end');
      setSelectedDateRange({
        from,
        to: undefined,
      });
      setSelectedTimeRange({
        from: selectedTimeRange.from,
        to: undefined,
      });
    }

    if (focused === 'end' && (!from || inputValue.start === '')) {
      setFocused('start');
      setSelectedDateRange({
        from: undefined,
        to,
      });
      setSelectedTimeRange({
        from: undefined,
        to: selectedTimeRange.to,
      });
    }
  };

  const MonthsComponent = useCallback(
    (props: MonthsProps) => <Months {...props} onTableMouseLeave={() => setPlaceholderValue('')} />,
    [],
  );

  const renderToCalendar = () => {
    return (
      <DayPickerBase
        cellSize={cellSize}
        displayMode={displayMode}
        locale={datePickerLocale}
        mode='range'
        className={classNames('pds-date-range-picker', className)}
        fromMonth={minDate}
        toMonth={maxDate}
        disabled={disabledDays}
        fixedWeeks
        showOutsideDays
        modifiers={{
          hoverOutsideRange: getRangeHoveredList(),
          hoverInsideRange: getRangeInsideHoveredList(),
        }}
        modifiersClassNames={{
          hoverOutsideRange: 'range-hover',
          hoverInsideRange: 'range-hover-inside',
        }}
        numberOfMonths={numberOfMonths}
        // @ts-expect-error: TS2345
        selected={selectedRangeLabel}
        month={currentMonth}
        onMonthChange={(shiftedMonth) => {
          setCurrentMonth(shiftedMonth);
        }}
        onDayClick={(day: Date, modifiers: DayModifiers) => {
          handleChangeDate(calculateTimeForOnDayClick(day), modifiers);
        }}
        onDayMouseEnter={(date) => {
          if (focused === 'start') {
            setPlaceholderValue(format(convertDateToDateWithTime(date, selectedTimeRange.from), dateFormat));
          } else {
            setPlaceholderValue(format(convertDateToDateWithTime(date, selectedTimeRange.to), dateFormat));
          }
        }}
        components={{
          Caption: (props) => (
            <Caption
              {...props}
              onClickCaptionLabel={(type: CaptionLabelType) => setCalendarType(type)}
              multipleMonths={!isBottomSheetPicker && !(numberOfMonths === 1)}
              locale={datePickerLocale}
              displayMode={displayMode}
            />
          ),
          // TODO: react-day-picker v9 업데이트시 MonthGrid로 대체
          Months: MonthsComponent,
        }}
        footer={
          (isBottomSheetPicker || showTimePicker) && (
            <Stack direction='column' mt={16} gap={16}>
              {showTimePicker && (
                <TimePicker
                  {...timePickerProps}
                  usePortal={false}
                  readOnly={isBottomSheetPicker}
                  size='medium'
                  value={focused === 'start' ? selectedTimeRange.from : selectedTimeRange.to}
                  fill={isBottomSheetPicker}
                  // @ts-expect-error: TS2322
                  disabledTime={disabledTime}
                  onChange={(time) => handleChangeTime({ time, isToday: false })}
                  onOpen={() => setTimePickerFocused(true)}
                  onClose={() => setTimePickerFocused(false)}
                />
              )}
              {isBottomSheetPicker && (
                <Button
                  fill
                  kind='outlined_primary'
                  onClick={() => {
                    if (showTimePicker) {
                      handleChangeTime({ time: null, isToday: true });
                    } else {
                      handleDataPresetRangeClick({ range: defaultDatePreset[0].range, id: 0 });
                    }
                  }}
                  disabled={disabledNowButton}
                >
                  {showTimePicker ? locale?.DatePicker.footer.now : locale?.DatePicker.footer.today}
                </Button>
              )}
            </Stack>
          )
        }
      />
    );
  };

  const renderToFooter = () => {
    const {
      container: { style, ...footerProps },
    } = footer;
    return (
      <Stack gap={8} {...footerProps} css={style}>
        <Button
          onClick={() => {
            handleInitial(false);
            handleClose();
          }}
          {...footer.button}
        >
          {locale?.DatePicker.footer.clear}
        </Button>
        <Button
          kind='primary'
          disabled={!(modifiers.start && modifiers.end)}
          onClick={() => {
            if (modifiers) {
              onChange?.(modifiers.start ?? undefined, modifiers.end ?? undefined);
              handleClose();
            }
          }}
          {...footer.button}
        >
          {locale?.DatePicker.footer.ok}
        </Button>
      </Stack>
    );
  };

  const renderToTimePickerFooter = () => {
    const {
      container: { style, ...footerProps },
    } = footer;
    return (
      <Stack gap={8} {...footerProps} css={style}>
        <Button
          onClick={() => {
            handleInitial(false);
            handleClose();
          }}
          {...footer.button}
        >
          {locale?.DatePicker.footer.clear}
        </Button>
        <Button
          kind='primary'
          disabled={
            (focused === 'start' && !inputValue.start) ||
            (focused === 'end' && !inputValue.end) ||
            disabledSelectedTimeButton
          }
          onClick={() => handleTimePickerFooterClick()}
          {...footer.button}
        >
          {locale?.DatePicker.footer.ok}
        </Button>
      </Stack>
    );
  };

  useEffect(() => {
    const { start, end } = inputValue;

    if ((start && !checkValidation(start)) || (end && !checkValidation(end))) {
      setErrorStatus(true);

      if (focused === 'start' && isMatch(start, dateFormat)) {
        setCurrentMonth(convertStringToDate(start));
      }
      if (focused === 'end' && isMatch(end, dateFormat)) {
        setCurrentMonth(convertStringToDate(end));
      }
    } else {
      setErrorStatus(false);

      if (!focused) {
        return;
      }

      if (
        (focused === 'start' && start.length < dateFormat.length) ||
        (focused === 'end' && end.length < dateFormat.length)
      ) {
        return;
      }

      const startDate = convertStringToDate(start);
      const endDate = convertStringToDate(end);

      setCurrentMonth(
        focused === 'start' || isSameMonth(startDate, endDate) ? startDate : subMonths(endDate, numberOfMonths - 1),
      );

      const compareStartDate = convertDateToDateWithTime(startDate);
      const compareEndDate = convertDateToDateWithTime(endDate);

      if (focused === 'start' && end && isAfter(compareStartDate, compareEndDate)) {
        setInputValue({
          ...inputValue,
          end: '',
        });
      }
      if (focused === 'end' && start && isBefore(compareEndDate, compareStartDate)) {
        setInputValue({
          ...inputValue,
          start: '',
        });
      }
    }
  }, [inputValue]);

  const renderToInput = () => (
    <Stack
      direction='column'
      width={width}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          if (!opened) {
            setOpened(true);
          } else if (focused && checkValidation(inputValue[focused], true)) {
            if (showTimePicker && disabledSelectedTime) {
              return;
            }
            // @ts-expect-error: TS2345
            handleChangeDate(convertStringToDate(inputValue[focused]), modifiers);
          }
        }
      }}
      onMouseEnter={() => showRemoveButton && setShowRemoveIcon(true)}
      onMouseLeave={() => showRemoveButton && setShowRemoveIcon(false)}
      onClick={() => {
        if (isBottomSheetPicker) {
          setOpened(true);
        }

        if (mergedDisabled[0] === true && mergedDisabled[1] === false) {
          setFocused('end');
        }

        !mergedDisabled[0] && !mergedDisabled[1] && setOpened(true);
      }}
      gap={semantic_spacing.helper_text.gap.desktop}
    >
      <DateRangeInput
        disabled={mergedDisabled}
        className={className}
        readOnly={isBottomSheetPicker}
        status={Boolean(error) || errorStatus || disabledSelectedTime ? 'error' : 'info'}
        size={size}
        placeholder={placeholderValue}
        inputValue={inputValue}
        fetchInputValue={setInputValue}
        showRemoveIcon={Boolean(inputValue.start && inputValue.end && showRemoveIcon)}
        onClickRemoveButton={() => handleInitial(true)}
        focused={focused}
        setFocused={(focused) => setFocused(focused)}
        modalOpen={Boolean(opened)}
        isTimePickerFocused={timePickerFocused}
      />
      {disabledSelectedTime && <FormHelperText status='error'>다른 시간을 선택해주세요.</FormHelperText>}
    </Stack>
  );

  const pickerDisplay = (
    <DatePickerDisplay
      displayMode={displayMode}
      className={className}
      disabled={mergedDisabled[0] && mergedDisabled[1]}
      zIndex={zIndex}
      input={renderToInput()}
      leftContent={
        showDatePreset &&
        displayMode === 'popover' && (
          <DataRangePreset
            items={defaultDatePreset.map((date) => {
              const diffDays = differenceInDays(date.range[1], date.range[0]);
              return { ...date, value: date.id, disabled: diffDays === 0 && disabledDatePreset };
            })}
            selected={selectedRangePreset ?? undefined}
            onChange={({ label, range, id }) => handleDataPresetRangeClick({ label, range, id, close: onClose })}
          />
        )
      }
      content={calendarType === 'day' ? renderToCalendar() : renderToYearMonthCalendar()}
      footer={(() => {
        if (displayMode === 'popover') {
          if (calendarType === 'day') {
            if (!isShowTodayButton && !isUnControlled && !isShowTimePickerFooter) {
              return null;
            }
            return (
              <Stack
                direction='row'
                {...footerProps}
                css={footerStyle}
                width={showDatePreset ? 'auto' : '100%'}
                justify='space-between'
              >
                {isShowTodayButton && (
                  <Button
                    kind='outlined_primary'
                    disabled={disabledDatePreset || disabledNowButton}
                    onClick={() => {
                      handleDataPresetRangeClick({
                        range: defaultDatePreset[0].range,
                        id: 0,
                        close: onClose,
                      });
                    }}
                  >
                    {locale?.DatePicker.footer.today}
                  </Button>
                )}
                {isUnControlled && renderToFooter()}
                {isShowTimePickerFooter && (
                  <>
                    <Button
                      kind='outlined_primary'
                      onClick={() => {
                        handleChangeTime({ time: null, isToday: true });
                      }}
                      disabled={disabledNowButton}
                    >
                      {locale?.DatePicker.footer.now}
                    </Button>
                    {renderToTimePickerFooter()}
                  </>
                )}
              </Stack>
            );
          }
          return null;
        } else {
          return showTimePicker ? renderToTimePickerFooter() : renderToFooter();
        }
      })()}
      opened={opened}
      onOpen={() => {
        setOpened(true);
        onOpen?.();
      }}
      onClose={() => {
        setOpened(false);
        onClose?.();
      }}
      onDismiss={() => {
        handleClose();
      }}
    />
  );

  if (showRadioGroupDatePreset) {
    return (
      <Stack direction='row' gap={8} align='center'>
        <RadioGroup
          disabled={mergedDisabled[0] && mergedDisabled[1]}
          size={size}
          radioItemType='box'
          value={selectedRangePreset}
          items={defaultDatePreset.map((date) => {
            const diffDays = differenceInDays(date.range[1], date.range[0]);
            return { ...date, value: date.id, disabled: diffDays === 0 && disabledDatePreset };
          })}
          onChange={(value) =>
            handleDataPresetRangeClick({
              range: typeof value === 'number' ? defaultDatePreset[value].range : undefined,
              id: value ?? undefined,
            })
          }
        />
        {pickerDisplay}
      </Stack>
    );
  }
  return pickerDisplay;
};
